/**
 * Stripe Payment
 */

import React, { useState, useEffect } from 'react';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  PaymentRequestTokenEvent,
  PaymentRequestCompleteStatus,
  PaymentRequest,
} from '@stripe/stripe-js';

import { processStripeOrder } from 'utils/helpers/paymentHelpers';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT } from 'utils/constants/dataTest';

import * as S from './styles';

type StripePaymentRequestButtonType = 'default' | 'book' | 'buy' | 'donate';
type StripePaymentRequestButtonTheme = 'dark' | 'light' | 'light-outline';

type Props = {
  cartTotal: number;
  setTOS?: Function;
  cartNotEmpty?: boolean;
  onPaymentConfirm?: Function;
  optionType?: StripePaymentRequestButtonType;
  optionTheme?: StripePaymentRequestButtonTheme;
  isInCart?: boolean;
  isInCheckout?: boolean;
  label: string;
  handlePaymentConfirm?: Function;
};

export const StripePayment: React.FC<Props> = ({
  cartTotal,
  cartNotEmpty = true,
  setTOS,
  onPaymentConfirm = (cb: Function) => cb(),
  optionType = 'buy',
  optionTheme = 'dark',
  isInCart = false,
  isInCheckout = false,
  label,
  handlePaymentConfirm,
}: Props) => {
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>();
  const [canMakePayment, setCanMakePayment] = useState(false);

  const stripe = useStripe();

  useEffect(() => {
    paymentRequest &&
      paymentRequest.update({
        total: {
          label: 'Everly Wellness, Inc',
          amount: cartTotal,
        },
      });
  }, [paymentRequest, cartTotal]);

  useEffect(() => {
    canMakePayment && cartNotEmpty && setTOS && setTOS(true);
  }, [canMakePayment, cartNotEmpty]);

  useEffect(() => {
    if (!stripe) return;
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Everly Wellness, Inc',
        amount: cartTotal,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: true,
      shippingOptions: [
        {
          id: 'free-shipping',
          label: 'Free shipping',
          detail: 'Arrives in 2 to 3 business days',
          amount: 0,
        },
      ],
    });

    pr.canMakePayment().then((result) => {
      setCanMakePayment(!!result);
      if (result) {
        setPaymentRequest(pr);
      }
    });

    pr.on('token', (event: PaymentRequestTokenEvent) => {
      const { token, complete, ...data } = event;
      onPaymentConfirm(() => {
        ewAnalytics.track({
          event: ANALYTICS.EVENTS.EXPRESS_PAY,
          data: {
            label: data.walletName,
          },
        });
        // Specific for the checkout page so we can pre-fill form before submitting
        if (isInCheckout && handlePaymentConfirm) {
          handlePaymentConfirm(data, token);
          complete('success');
        } else {
          processStripeOrder(data, token, label).then(
            (response: PaymentRequestCompleteStatus) => {
              complete(response);
            },
          );
        }
      });
    });

    pr.on('cancel', function () {
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: ANALYTICS.LABELS.EXPRESS_PAY_CANCEL,
        },
      });
    });
  }, [stripe]);

  // https://stripe.com/docs/js/appendix/style
  // https://stripe.com/docs/stripe-js/elements/payment-request-button#html-js-styling-the-element
  const options = {
    style: {
      paymentRequestButton: {
        type: optionType,
        theme: optionTheme,
        height: '48px',
      },
    },
  };

  const trackClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.EXPRESS_PAY_CHECKOUT,
      },
    });
  };

  return canMakePayment && paymentRequest && cartNotEmpty ? (
    <S.StripePaymentWrapper
      isInCart={isInCart}
      isInCheckout={isInCheckout}
      data-testid={CHECKOUT.STRIPE_EXPRESS_PAY}
    >
      <PaymentRequestButtonElement
        options={{ ...options, paymentRequest }}
        className="payment-request-btn"
        onClick={trackClick}
      />
    </S.StripePaymentWrapper>
  ) : null;
};

export default StripePayment;
